// Which of the six nav steps the Author can reach, and where step 6 goes.
// Pure functions over BookDetail so tests/client/steps.test.ts can drive every
// Book state without a DOM, and so BookLayout, NavSteps, and the Book page all
// read one answer.
import type { BookDetail, JobKind } from "@contract";
import type { StepKey } from "./keys";

/** Step 6 resolves to one of three sub-routes; each stays directly addressable. */
export type Step6Segment = "render" | "render/progress" | "audiobook";

/**
 * Where "6 Vertonen" points for this Book: the Audiobook once one is
 * assembled, the running render while it runs, the Estimate otherwise. The
 * Audiobook wins over a render in flight only when nothing is rendering, so a
 * re-render of one Chapter takes the Author to the progress view.
 */
export function step6Segment(book: BookDetail | undefined): Step6Segment {
  const status = book?.render?.status;
  if (status === "rendering" || status === "stopping" || status === "assembling") {
    return "render/progress";
  }
  if (book?.audiobook) return "audiobook";
  return "render";
}

/**
 * Why a step is not reachable yet. Each is a key under nav:stepLock.*, and
 * scripts/check-i18n.ts checks the list against the catalogue, so a reason
 * without a sentence fails the build.
 */
export const STEP_LOCK_REASONS = [
  "needsParse",
  "needsStyleSheet",
  "needsScript",
  "needsConfirmation",
  "needsApproval",
  "needsCasting",
] as const;
export type StepLockReason = (typeof STEP_LOCK_REASONS)[number];

const hasScript = (book: BookDetail): boolean =>
  book.chapters.some((chapter) => chapter.versionCount > 0);

/**
 * What stopped a Failed Book: the kind of the job its retry button targets.
 * Null while the Book is not Failed, or when the payload names no retry job.
 */
export function failedJobKind(book: BookDetail): JobKind | null {
  if (book.state !== "failed" || !book.retryJobId) return null;
  return book.activeJobs.find((job) => job.id === book.retryJobId)?.kind ?? null;
}

/**
 * Whether the Source has been read into a Manuscript. A Book that Failed in
 * Parse never produced one, so the Chapter list stays locked: only Parse
 * failing keeps it shut, because a Chapter Proposal that failed still leaves a
 * Manuscript, and its own retry lives on that screen.
 */
const isParsed = (book: BookDetail): boolean =>
  book.state !== "uploaded" && failedJobKind(book) !== "parse";

/**
 * The Author may render before every Chapter is Approved: DESIGN "What the
 * Author does" and CONTEXT's Render entry both say a single Approved Chapter
 * can be rendered on its own once Casting is complete. So step 6 opens on the
 * first Approved Chapter plus a cast Narrator, and stays open once a render or
 * an Audiobook exists whatever the Book's state went back to.
 */
/**
 * The reason step `key` is locked, or null when it is open. "Meaningful" is
 * the DESIGN rule per step: the Chapter list needs a Manuscript, the Style
 * Sheet needs the pass that writes it, Review needs a Narration Script,
 * Casting needs a confirmed Book to sample from, and the render screen needs
 * one Approved Chapter and a cast Narrator.
 */
export function stepLock(key: StepKey, book: BookDetail | undefined): StepLockReason | null {
  if (!book) return null;
  switch (key) {
    case "step1":
      return null;
    case "step2":
      return isParsed(book) ? null : "needsParse";
    case "step3":
      return book.styleSheet ? null : "needsStyleSheet";
    case "step4":
      return hasScript(book) ? null : "needsScript";
    case "step5":
      return book.confirmedAt ? null : "needsConfirmation";
    default:
      return book.confirmedAt ? null : "needsConfirmation";
  }
}

/** Chapters waiting for the Author in Review; the badge on step 4. */
export const chaptersAwaitingReview = (book: BookDetail | undefined): number =>
  book?.counts.inReview ?? 0;
